import { IFormStep } from '../../utils/formStep';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { TFunction, withTranslation } from 'react-i18next';
import ProfessionalStep1PracticeLocation from './professionalStep1PracticeLocation';
import useForceUpdate from '../../../../forceUpdate';
import fetchFormData, { IFormDataModel } from '../../../../helpers/fetchFormData';
import {
    ILocationModel,
    LocationModel,
    locationModelFromFormData,
} from '../../../../helpers/models/locationModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import getProvinces from '../../../../helpers/provinces';
import { updateLocations } from '../../../../helpers/location';
import { updateAccount } from '../../../../helpers/account';
import {
    handleRequiredError,
    scrollToFormErrors,
    validatePhoneExt,
    validatePhoneNumber,
    validatePostalCode,
    validatePca,
} from '../../utils/formErrors';
import {
    IProfessionalStep1FormResultModel,
    ProfessionalStep1FormResultKeys,
} from './professionalStep1FormModel';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { INotificationErrorMessage } from '../../../notification/notification';
import FormDescriptionModal from '../../../modal/instances/formDescriptionModal';
import { IFormModalRef } from '../../../modal/modal';
import FormSoftwareModal from '../../../modal/instances/formSoftwareModal';
import { startOverId } from '../../registration';
import { listenToAccessibilityKeyboardInputs } from '../../../../accessibleNavigation';

const ProfessionalStep1 = (props: IFormStep) => {
    const { t, startOver, account, i18n } = props;

    const locations = useRef<LocationModel[]>(
        account.locations.length > 0
            ? (account.locations as LocationModel[])
            : [new LocationModel()]
    );
    const [editedLocations, setEditedLocations] = useState<LocationModel[] | undefined>();
    const forceUpdate = useForceUpdate();
    const [provinces, setProvinces] = useState<IProvinceModel[]>([]);
    const [introModal, setIntroModal] = useState<JSX.Element | undefined>();
    const [skipSoftwareCommunicationCheck, setSkipSoftwareCommunicationCheck] =
        useState<boolean>(false);
    const ref = React.createRef<IFormModalRef>();
    useEffect(() => {
        if (skipSoftwareCommunicationCheck) {
            ref.current?.setModalOpen(false);
            document.getElementById('form_submit_button')!.click();
        }
    }, [skipSoftwareCommunicationCheck]);

    useEffect(() => {
        async function getData() {
            const provincesList = await getProvinces(i18n.language);
            if (provincesList) {
                setProvinces(provincesList);
            }

            if (!localStorage.getItem(account.id)) {
                setIntroModal(
                    <FormDescriptionModal name={account.firstname} type={account.type}/>
                );
                localStorage.setItem(account.id, 'seen');
            }
        }
        getData();
    }, [i18n.language]);

    const handleOnAddClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.currentTarget.blur();
        const newLocations = [...locations.current];
        newLocations.push(new LocationModel());
        locations.current = newLocations;
        forceUpdate();

        // Wait for update then adjust focus element
        setTimeout(() => {
            document
                .getElementById(
                    ProfessionalStep1FormResultKeys.COMPANY_NAME + `_${newLocations.length - 1}`
                )
                ?.focus();
            listenToAccessibilityKeyboardInputs(
                document.getElementById(
                    `form_professional_step_1_delete_${newLocations.length - 1}`
                )
            );
        }, 50);
    };

    const fetchProvidersFromData = (data: IFormDataModel[]) => {
        const dividedData: (
            | { name: any; value: any; checked: any }
            | { name: any; value: any; checked?: undefined }
            )[][] = [];

        locations.current.forEach((x, i) => {
            const filtered = data.filter((item) => item.name.endsWith(i.toString()));
            dividedData.push(filtered);
        });

        return dividedData;
    };

    const handleOnDeleteClick = (e: MouseEvent<HTMLImageElement>) => {
        e.preventDefault();

        const form = document.getElementById('professional-step-1-form')! as HTMLFormElement;
        const data = fetchFormData(form);
        const dividedData = fetchProvidersFromData(data);
        const locationsArray: ILocationModel[] = [];
        dividedData.forEach((item, i) => {
            item = item.map((x) => {
                if (x.checked) {
                    return {
                        name: x.name.slice(0, x.name.length - 2),
                        value: x.value,
                        checked: x.checked,
                    };
                }

                return { name: x.name.slice(0, x.name.length - 2), value: x.value };
            });

            const formResult = Object.assign(
                {},
                ...item.map((x) => ({ [x.name]: x.value }))
            ) as IProfessionalStep1FormResultModel;
            const location: ILocationModel = locationModelFromFormData(formResult);
            location.province = Number(
                provinces.find(
                    (province) =>
                        province.name === formResult.form_professional_step_1_field_province
                )?.id
            );

            locationsArray.push(location);
        });

        const newLocations = [...locationsArray];
        newLocations.splice(+e.currentTarget.dataset.id!, 1);
        form.reset();
        setEditedLocations(newLocations as LocationModel[]);
        locations.current = newLocations as LocationModel[];
        document.getElementById('button_add_item')!.focus();
    };

    const handleClientFormValidation = (
        formResult: IProfessionalStep1FormResultModel,
        data: IFormDataModel[],
        t: TFunction,
        index: number
    ) => {
        const errorList = handleRequiredError(formResult, data, t, index);

        if (formResult.form_professional_step_1_field_phone) {
            const phoneValidation = validatePhoneNumber(
                ProfessionalStep1FormResultKeys.PHONE + `_${index}`,
                formResult.form_professional_step_1_field_phone,
                t
            );
            if (phoneValidation) {
                errorList.push({
                    message: phoneValidation,
                    focusElement: ProfessionalStep1FormResultKeys.PHONE + `_${index}`,
                    weight: 1 + index * 8,
                });
            }
        }

        if (formResult.form_professional_step_1_field_phone_ext) {
            const phoneExtValidation = validatePhoneExt(
                ProfessionalStep1FormResultKeys.PHONE_EXT + `_${index}`,
                formResult.form_professional_step_1_field_phone_ext,
                t
            );
            if (phoneExtValidation) {
                errorList.push({
                    message: phoneExtValidation,
                    focusElement: ProfessionalStep1FormResultKeys.PHONE_EXT + `_${index}`,
                    weight: 2 + index * 8,
                });
            }
        }

        if (formResult.form_professional_step_1_field_postal_code) {
            const postalCodeValidation = validatePostalCode(
                ProfessionalStep1FormResultKeys.POSTAL_CODE + `_${index}`,
                formResult.form_professional_step_1_field_postal_code,
                t
            );
            if (postalCodeValidation) {
                errorList.push({
                    message: postalCodeValidation,
                    focusElement: ProfessionalStep1FormResultKeys.POSTAL_CODE + `_${index}`,
                    weight: 7 + index * 8,
                });
            }
        }

        const pcaValidation = validatePca(
            ProfessionalStep1FormResultKeys.PCA + `_${index}`,
            {
                address: formResult.form_professional_step_1_field_address,
                city: formResult.form_professional_step_1_field_city,
                postal_code: formResult.form_professional_step_1_field_postal_code,
            },
            t
        );
        if (pcaValidation) {
            errorList.push({
                message: pcaValidation,
                focusElement: ProfessionalStep1FormResultKeys.PCA + `_${index}`,
                weight: 6 + index * 8,
            });
        }

        return errorList;
    };

    const handleSubmitProfessionalStep1 = (e: any) => {
        e.preventDefault();
        const data = fetchFormData(e);
        const errorList: INotificationErrorMessage[] = [];

        const dividedData = fetchProvidersFromData(data);

        const locationsArray: ILocationModel[] = [];

        dividedData.forEach((item, i) => {
            item = item.map((x) => {
                if (x.checked) {
                    return {
                        name: x.name.slice(0, x.name.length - 2),
                        value: x.value,
                        checked: x.checked,
                    };
                }

                return { name: x.name.slice(0, x.name.length - 2), value: x.value };
            });

            const formResult = Object.assign(
                {},
                ...item.map((x) => ({ [x.name]: x.value }))
            ) as IProfessionalStep1FormResultModel;
            const location: ILocationModel = locationModelFromFormData(formResult);

            const itemErrors = handleClientFormValidation(formResult, data, t, i);

            // Append suffix on each error items
            itemErrors.forEach((error: INotificationErrorMessage) => {
                errorList.push({
                    messagePrefix: error.message,
                    message: 'form_multiple_error_suffix',
                    messageSuffix: (i + 1).toString(),
                    focusElement: error.focusElement,
                    weight: error.weight,
                });
            });

            location.province = Number(
                provinces.find(
                    (province) =>
                        province.name === formResult.form_professional_step_1_field_province
                )?.id
            );

            locationsArray.push(location);
        });

        if (!errorList.length) {
            for (let i = 0; i < locationsArray.length; ++i) {
                const softwareSelectElement = document.getElementById(
                    `${ProfessionalStep1FormResultKeys.MANAGING_SOFTWARE}_${i}`
                )! as HTMLSelectElement;
                if (
                    !skipSoftwareCommunicationCheck &&
                    !locationsArray[i].agreeManagementSoftware &&
                    softwareSelectElement.options[softwareSelectElement.selectedIndex].getAttribute(
                        'data-datatype'
                    ) === 'enabled'
                ) {
                    ref.current?.setModalOpen(true);
                    return;
                }
            }

            // update locations
            updateLocations(i18n.language, +account.id, locationsArray);

            // update step in account to test
            const newFormData = Object.assign({}, account);
            if (newFormData.step >= 1) {
                newFormData.step++;
            }

            // temporary since set in BE
            newFormData.dateLastAction = new Date().toISOString().split('T')[0];
            // set number to province instead of the returned object
            newFormData.province = +(account.province as IProvinceModel).id;
            newFormData.role = +(account.role as IRoleModel).id;
            updateAccount(i18n.language, account.id, { ...newFormData });
        }

        if (errorList.length) {
            props.setError(errorList);
            scrollToFormErrors();
        }
    };

    return (
        <>
            {introModal}
            <FormSoftwareModal
                onCloseAndContinue={(e: any) => setSkipSoftwareCommunicationCheck(true)}
                ref={ref}
            />
            <form
                className="registration-container"
                id="professional-step-1-form"
                onSubmit={handleSubmitProfessionalStep1}
            >
                {locations.current.map((item, index) => {
                    return (
                        <div key={`professional_step_1_${index}`}>
                            {index > 0 && (
                                <hr
                                    key={`professional_step_1_title_${index}`}
                                    className="registration-professional-section-divider"
                                />
                            )}
                            <ProfessionalStep1PracticeLocation
                                key={`professional_step_1_form_${index}`}
                                index={index}
                                account={props.account}
                                handleOnDeleteClick={handleOnDeleteClick}
                                locationsOverwrite={editedLocations}
                            />
                        </div>
                    );
                })}
                <button
                    className="button button--secondary registration-professional-button"
                    onClick={handleOnAddClick}
                    id="button_add_item"
                >
                    {t('form_professional_step_1_button_add_location')}
                </button>
                <div className="registration-professional-button-container">
                    <button
                        className="button button--secondary registration-professional-button"
                        onClick={startOver}
                        id={startOverId}
                    >
                        {t('form_professional_step_1_button_restart')}
                    </button>
                    <button
                        className="button button--primary registration-professional-button"
                        type="submit"
                        id="form_submit_button"
                    >
                        {t('form_professional_step_1_button_next')}
                    </button>
                </div>
            </form>
        </>
    );
};

export default withTranslation()(ProfessionalStep1);
